/* De qué talonario es un vale de urea.
 *
 * ── DE DÓNDE SALE ────────────────────────────────────────────────────────────
 *
 * Igna, 15/09, después de que trece vales de Río Grande no se guardaran:
 * *«¿dónde se guardan esos vales en el Hub? ¿Tenemos un sector "Río Grande"?
 * ¿O cómo lo encuentro?»*.
 *
 * Se guardan en la misma tabla que el resto —`urea_cargas`— con una columna
 * `serie` que dice de cuál de los dos talonarios salió el vale. La columna
 * existe desde el 15/09, el servicio la trae… y ninguna pantalla la mostraba.
 * O sea que los vales de Río Grande iban a caer mezclados con los de acá y no
 * había forma de decir cuál era cuál.
 *
 * ── UNA SOLA PANTALLA CON FILTRO, Y NO DOS PANTALLAS ────────────────────────
 *
 * Pañol sí tiene una entrada por sede, y está bien: ahí el stock es
 * físicamente otro y la pregunta es siempre de UNA sede. Acá no — los vales
 * son una sola lista, el control de la secuencia se hace sobre ella, y dos
 * pantallas harían que cada una muestre la mitad de un mismo control.
 *
 * ── PERO LA SECUENCIA SE CONTROLA POR TALONARIO ─────────────────────────────
 *
 * Y esto NO es cosmético. El aviso de «faltan vales en la secuencia» se
 * calculaba sobre todos los números juntos, con un tope: si el rango pasaba de
 * 200 no listaba nada, «seguro son varios talonarios». El talonario de acá va
 * por el 9352 y el de Río Grande arranca en el 6573: el día que entrara el
 * primer vale de allá, el control de la secuencia de acá se apagaba entero y
 * en silencio. No falla, contesta menos.
 */

import { diaAr, diaIsoAr } from "./fechaAr";

/* El de acá no lleva serie —es el talonario original— y por eso `null` no es
   un dato que falta: es «el de la oficina». */
export const BUE = "bue";
export const RGD = "rgd";

export const TALONARIOS = [
  { id: "todos", rotulo: "Todos" },
  { id: BUE, rotulo: "Buenos Aires", corto: "BUE" },
  { id: RGD, rotulo: "Río Grande", corto: "RGD" },
];

export const talonarioDe = (fila) =>
  String(fila?.serie || "").trim().toUpperCase() === "RGD" ? RGD : BUE;

export const rotuloDe = (fila) =>
  talonarioDe(fila) === RGD ? "Río Grande" : "Buenos Aires";

export const enElTalonario = (filas, id) =>
  (!id || id === "todos")
    ? (filas || [])
    : (filas || []).filter((f) => talonarioDe(f) === id);

/* ── El control de la secuencia, POR TALONARIO ─────────────────────────────── */

/* Cuántos números de distancia toleramos antes de dejar de listar. Un rango
   enorme dentro de UN talonario sigue siendo ruido —son meses de vales— y
   listarlos no ayuda a nadie. Lo que ya no puede pasar es que el otro
   talonario infle ese rango y apague el aviso. */
const RANGO_MAXIMO = 200;

/* Duplicados y huecos de UNA numeración. Exportada porque el talonario de
 * gasoil de la base (`routes/Gasoil`) se controla igual: es otra serie, con
 * sus propios números, y la regla —el duplicado grita, el hueco avisa— es la
 * misma. Dos copias de este cálculo terminarían contestando distinto sobre el
 * mismo papel.
 *
 * `anulados`: números que se sabe que no van a aparecer nunca (anulados o
 * sin usar). No son faltantes: listarlos convierte el aviso en ruido. */
export function controlDeSecuencia(filas, { anulados = [] } = {}) {
  const vals = (filas || [])
    .map((c) => (c.vale == null ? "" : String(c.vale).trim()))
    .filter((v) => v !== "");

  const cuenta = {};
  vals.forEach((v) => { cuenta[v] = (cuenta[v] || 0) + 1; });
  const duplicados = Object.entries(cuenta)
    .filter(([, n]) => n > 1)
    .map(([vale, n]) => `${vale} (×${n})`);

  const nums = vals
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n))
    .map((n) => Math.trunc(n));

  const fuera = new Set((anulados || []).map((n) => Math.trunc(Number(n))));
  const faltantes = [];
  if (nums.length >= 2) {
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    if (max - min <= RANGO_MAXIMO) {
      const hay = new Set(nums);
      for (let i = min + 1; i < max; i++) {
        if (!hay.has(i) && !fuera.has(i)) faltantes.push(i);
      }
    }
  }
  return { duplicados, faltantes };
}

/* Los vales cargados MÁS DE UNA VEZ en lo que se está mirando, por talonario,
 * en el orden de `TALONARIOS`. Sólo los talonarios que tengan alguno.
 *
 * Igna, 04/09: cargaron el 9265 como si fuera el 9266 → el 9265 quedó dos veces.
 * El duplicado se sigue contando sobre el PERÍODO —es un error de carga que se
 * ve en la tabla de abajo—, pero los faltantes ya no: ésos son la cuenta del
 * parte, desde el corte (`contarTalonario`, más abajo). Antes esta función
 * daba también los huecos del período, con tope de 200 números, y eso era un
 * «faltan» distinto del que manda el bot.
 *
 * DOS TALONARIOS PUEDEN REPETIR EL MISMO NÚMERO sin que eso sea un duplicado:
 * son dos papeles distintos con el mismo número impreso. Contados juntos, el
 * día que las series se crucen el aviso señalaría un error que no existe — y
 * un aviso que grita en falso se deja de mirar.
 */
export function duplicadosPorTalonario(filas) {
  const out = [];
  for (const t of TALONARIOS) {
    if (t.id === "todos") continue;
    const mias = enElTalonario(filas, t.id);
    if (!mias.length) continue;
    const { duplicados } = controlDeSecuencia(mias);
    if (duplicados.length) out.push({ id: t.id, rotulo: t.rotulo, duplicados });
  }
  return out;
}

/* ══ CUÁNTOS VALES FALTAN: LA MISMA CUENTA QUE EL PARTE ══════════════════════
 *
 * Nico, 11/10/2026. El parte de urea decía *«Faltan 86089 vales (243 de 86332
 * cargados, serie 9233–95564)»*, e Igna: *«no hay chances de que falten TANTOS
 * vales»*. Los fabricaba UN vale mal leído —«95564», cargado el 08/10 entre
 * el 9562 y el 9563 con la fecha del papel leída como 08/09— que estiraba la
 * serie hacia arriba. Su número real no se sabe y no se inventa. Nico pidió
 * poder reiniciar el conteo desde un vale: es «/valesdeurea-reset» en el bot.
 *
 * ESTA PANTALLA CONTABA OTRA COSA: los huecos entre el primer y el último vale
 * del PERÍODO que se estaba mirando. El parte cuenta desde el corte hasta el
 * último cargado. Eran dos «faltan» para el mismo talonario, y el que abre el
 * hub después de leer el parte no tiene cómo saber cuál creer. Ahora es uno.
 *
 * ── MÓDULO APAREADO CON `urea.huecos` (fleet-bot-pastor) ────────────────────
 *
 * El corte lo devuelve `urea_corte_vales()` (la migración del 11/10), que lee
 * las claves del bot y resuelve cuál manda. El contrato está en el
 * ARQUITECTURA.md de los dos repos. Lo que se cuenta acá:
 *
 *   · POR VALE (hay ancla): todas las cargas del talonario, sin mirar la fecha;
 *     entran los números ≥ ancla; la serie arranca EN el ancla aunque no esté
 *     cargado; se recorre de menor a mayor desde ancla−1 y al primer salto de
 *     más de `LEJOS_DE_LA_SERIE` ése y los siguientes quedan fuera de la serie.
 *     Un número más de mil por DEBAJO del ancla cargado desde el día del
 *     reinicio también queda fuera (un «956» por «9567»). Las cargas sin
 *     número cuentan como «sin vale» si se cargaron del día del reinicio en
 *     adelante. «Cargado» es el día de `creado_en` (`diaDeCarga`).
 *   · POR FECHA (no hay ancla, o la fecha se movió después): las cargas con
 *     `fecha` ≥ el corte, menos las de fecha adelantada cargadas antes del
 *     corte; la serie es un tramo (`separarSerie`).
 *   · Lo que queda fuera se separa en tramos: tres vales seguidos o más son
 *     OTRO TALONARIO (`otros_talonarios`), nunca «a revisar»; el resto va a
 *     revisar (`fuera_de_serie`).
 *   · En los dos: faltan los números entre el primero y el último que no
 *     están, menos los anulados vivos. Lo que todavía no llegó no es un vale
 *     perdido: no se cuenta más allá del último cargado.
 *
 * Un cambio a esta cuenta es un cambio a `urea.huecos`, y al revés. Si difieren,
 * el hub y el parte dicen dos números distintos del mismo papel, que es
 * exactamente lo que esto vino a sacar.
 */

/* El piso: nunca se cuenta desde antes del 1/9 (Nico, 05/09: «purgar lo que
   faltó de agosto»). Es `urea.VALES_DESDE`. Se usa si el corte no se pudo
   leer: sin corte se cuenta desde el piso y se ven MÁS faltantes, nunca menos,
   que es la misma dirección que toma el bot. */
export const VALES_DESDE = "2026-09-01";

/* ── LA REGLA DE LOS VALES RAROS, EN UN SOLO LUGAR ───────────────────────────
 *
 * Cuántos números puede saltar el talonario de un vale cargado al siguiente
 * antes de que el salto deje de ser «vales sin cargar» y pase a ser un número
 * mal leído. Es `urea.LEJOS_DE_LA_SERIE` del bot (1.000): el talonario avanza
 * de a uno, unos 200 vales por mes, y mil números de golpe no son un atraso,
 * son el «95564» de un talonario que va por el 9579.
 *
 * Lo que queda del otro lado del salto NO SE BORRA NI SE CORRIGE: sale de la
 * cuenta y se muestra aparte, «a revisar», con el link a la carga. Su número
 * real no se sabe y no se inventa; lo corrige el que mira la foto.
 *
 * ES LA MISMA REGLA DEL BOT, EXACTA (`urea.separar_serie`, `urea.huecos`):
 * los tramos, los tres vales de un talonario, el desempate por las últimas 50
 * cargas, la fecha adelantada contra el día de carga y la regla de abajo del
 * ancla. El contrato está en el ARQUITECTURA.md de los dos repos; si el bot
 * cambia una, se cambia acá. */
export const LEJOS_DE_LA_SERIE = 1000;

/* Cuántos vales seguidos hacen un TALONARIO (`urea.MIN_TALONARIO`). Los
   números mal leídos vienen sueltos: tres seguidos son papeles de verdad, y
   un talonario nunca va «a revisar» (revisión del 11/10: siete vales de un
   talonario nuevo le ganaban la serie a los seis de siempre, que pasaban a
   «a revisar» con sus faltantes escondidos). */
export const MIN_TALONARIO = 3;

/* Cuántas cargas recientes (por `id`) desempatan la serie
   (`urea.RECIENTES`). */
export const RECIENTES = 50;

/* Desde cuántos vales «/valesdeurea-reset desde el vale N» reconoce un
   talonario sin pedir «igual» (`urea_vales_reset.MIN_TRAMO`). Sólo para
   escribir el comando exacto. */
export const MIN_TRAMO_COMANDO = 5;

/* La serie de cada talonario, escrita como la filtra el bot: el de Buenos
   Aires es `serie IS NULL`, el de Río Grande `serie = 'RGD'`. */
export const SERIE = { [BUE]: null, [RGD]: "RGD" };

/* El número de un vale: el primer grupo de dígitos («A-9560» → 9560). Es
   `urea._numero_de`. */
export function numeroDeVale(vale) {
  const m = String(vale ?? "").match(/\d+/);
  return m ? Number(m[0]) : null;
}

/* EL DÍA DE CARGA: el día argentino de `creado_en`, que pone la base y no se
 * lee de ningún papel (sin `creado_en`, el de la fecha). Es `urea.dia_de_carga`.
 * El corte por vale lo usa para «cargado después del reinicio»: la fecha del
 * papel sale mal también hacia atrás (los 9561–9566 entraron el 08/10 «del
 * 08/09»), y con ella un número mal leído por abajo desaparecía sin rastro. */
export function diaDeCarga(fila) {
  return diaIsoAr(fila?.creado_en) || diaIsoAr(fila?.fecha) || "";
}

/* UNA FECHA ADELANTADA: la del papel cae en un día POSTERIOR al día en que se
 * cargó. Es `urea.fecha_adelantada`. Un vale no puede ser de un día que
 * todavía no llegó cuando se cargó: el 9401 entró «del 19/08/2029» el
 * 19/09/2026, y con esa fecha entraba en cualquier corte.
 *
 * SE COMPARA CON EL DÍA DE CARGA, NO CON HOY. Comparada con hoy, la misma
 * fila cambiaba de cuenta sola el día que llegaba la fecha del papel (un vale
 * «del 19/09» cargado el 14/09 quedaba afuera de un corte del 15/09 hasta el
 * 19/09 y adentro después), y el hub daba otro número que el parte. Y se
 * compara el DÍA argentino: el vale sin hora se guarda a las 12:00. */
export function fechaAdelantada(fila) {
  const d = diaIsoAr(fila?.fecha);
  const c = diaIsoAr(fila?.creado_en);
  return !!d && !!c && d > c;
}

/* La fecha con la que una carga entra en un corte por fecha: la del vale, o la
   de carga si la del vale está adelantada (`urea.dia_para_el_corte`). */
export function fechaQueCuenta(fila) {
  const f = fila?.fecha || null;
  return fechaAdelantada(fila) ? fila.creado_en : f;
}

/* El corte de UN talonario, como lo devolvió `urea_corte_vales()`.
 *
 * `cortes` es la lista que devolvió la base, o `null` si no se pudo leer. Sin
 * su fila, el talonario se cuenta desde el piso y `sin_leer` lo dice: la
 * pantalla tiene que poder avisar que no sabe si hubo un reinicio. */
export function corteDe(cortes, id) {
  const fila = Array.isArray(cortes)
    ? cortes.find((c) => c && c.talonario === id) : null;
  const fecha = String(fila?.desde_fecha || "").slice(0, 10);
  const vale = Number(fila?.desde_vale);
  const conAncla = Number.isInteger(vale) && vale > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(String(fila?.reinicio_dia || ""));
  return {
    desde_fecha: /^\d{4}-\d{2}-\d{2}$/.test(fecha) && fecha > VALES_DESDE
      ? fecha : VALES_DESDE,
    desde_vale: conAncla ? vale : null,
    reinicio_dia: conAncla ? fila.reinicio_dia : null,
    reinicio_por: conAncla ? (fila.reinicio_por || null) : null,
    sin_leer: !fila,
  };
}

/* Por vale: los que siguen la serie desde `piso`, y los que no. Es
   `urea._sin_saltos`: al primer salto de más de `LEJOS_DE_LA_SERIE` se corta,
   y ése y todos los de arriba quedan afuera. */
function sinSaltos(ordenados, piso) {
  const seguidos = [];
  let previo = piso - 1;
  for (let i = 0; i < ordenados.length; i++) {
    if (ordenados[i] - previo > LEJOS_DE_LA_SERIE) {
      return [seguidos, ordenados.slice(i)];
    }
    seguidos.push(ordenados[i]);
    previo = ordenados[i];
  }
  return [seguidos, []];
}

/* Los números (ordenados, sin repetidos) en tramos: seguidos mientras de uno
   al siguiente no haya más de `LEJOS_DE_LA_SERIE`. Es `urea.tramos`. */
export function tramos(ordenados) {
  const out = [];
  for (const n of ordenados || []) {
    const ultimo = out[out.length - 1];
    if (ultimo && n - ultimo[ultimo.length - 1] <= LEJOS_DE_LA_SERIE) ultimo.push(n);
    else out.push([n]);
  }
  return out;
}

const sigue = (tramo, ref) =>
  ref != null &&
  tramo[0] - LEJOS_DE_LA_SERIE <= ref && ref <= tramo[tramo.length - 1] + LEJOS_DE_LA_SERIE;

/* `[serie, sueltos, otros]`: es `urea.separar_serie`.
 *
 *   · Un tramo de `MIN_TALONARIO` vales o más es un talonario.
 *   · Los candidatos a serie son los talonarios (o todos los tramos, si no
 *     hay ninguno). Si hay más de uno, se queda con los que siguen a
 *     `referencia` —el tope de las últimas cargas, `topeDeLaSerie(recientes)`—
 *     y, entre ésos, el de más vales. A igual cantidad, el PRIMERO: el más
 *     alto, que era la regla, es justo el de la cifra de más en un 1 a 1
 *     entre un 9585 y un 95586.
 *   · Los tramos chicos que no son la serie van a revisar (`sueltos`); los
 *     talonarios que no son la serie, a `otros`. */
export function separarSerie(ordenados, referencia = null) {
  const ts = tramos(ordenados);
  if (!ts.length) return [[], [], []];
  const talonarios = ts.filter((t) => t.length >= MIN_TALONARIO);
  let cands = talonarios.length ? talonarios : ts;
  if (cands.length > 1) {
    const ref = typeof referencia === "function" ? referencia() : referencia;
    const siguen = cands.filter((t) => sigue(t, ref));
    if (siguen.length) cands = siguen;
  }
  const mas = Math.max(...cands.map((t) => t.length));
  const serie = cands.find((t) => t.length === mas);
  const sueltos = ts.filter((t) => t !== serie && t.length < MIN_TALONARIO).flat();
  const otros = talonarios.filter((t) => t !== serie);
  return [serie, sueltos, otros];
}

/* El tope de la serie de unas cargas (`urea.tope_de_la_serie`): el último
   número del tramo que gana, sin referencia. Con las últimas `RECIENTES`
   cargas por `id` es por dónde va el talonario que se está cargando. */
export function topeDeLaSerie(filas) {
  const nums = [...new Set((filas || [])
    .map((f) => (f?.vale ? numeroDeVale(f.vale) : null))
    .filter((n) => n !== null))].sort((a, b) => a - b);
  const [serie] = separarSerie(nums);
  return serie.length ? serie[serie.length - 1] : null;
}

const ultimoId = (filas) => {
  const ids = filas.map((f) => Number(f?.id)).filter(Number.isFinite);
  return ids.length ? Math.max(...ids) : -1;
};

/* LA CUENTA DE UN TALONARIO. Es `urea.huecos`, y devuelve los mismos campos
 * con los mismos nombres, para poder ponerlos al lado sin traducir nada.
 *
 * `filas`: las cargas de ESE talonario (`id`, `vale`, `fecha`, `creado_en`).
 *   Con ancla, todas; por fecha, alcanza con las de `fecha` ≥ el corte (las
 *   demás se descartan igual).
 * `corte`: lo que devuelve `corteDe`.
 * `anulados`: los números anulados vivos de ese talonario, o `null` si no se
 *   pudieron leer — que NO es «no hay»: se cuenta sin sacar nada y
 *   `anulados_sin_leer` lo dice.
 * `recientes`: las últimas `RECIENTES` cargas del talonario por `id` (con
 *   `vale`), o `null` si no se pudieron leer: desempatan la serie por fecha.
 *
 * EL CORTE POR FECHA ES EL MISMO QUE HACE EL BOT: `fecha >= 'YYYY-MM-DD'` en la
 * base, que lo lee como la medianoche UTC (las 21:00 de la víspera, acá). Se
 * replica tal cual: corregirlo de un solo lado haría que el hub y el parte
 * difieran justo en las cargas de esa noche. La fecha adelantada, en cambio, se
 * mira por DÍA argentino de carga, como el bot. */
export function contarTalonario(filas, corte, { anulados = [], recientes = null } = {}) {
  const c = corte || corteDe(null);
  const piso = c.desde_vale || null;
  const corteMs = Date.parse(`${c.desde_fecha}T00:00:00Z`);

  const numeros = new Map();      // número → la primera carga con ese número
  const abajo = new Map();        // con ancla: los de mil o más por debajo, cargados después
  let sinVale = 0;
  const noNumericos = [];
  for (const f of filas || []) {
    const v = f?.vale;
    const n = v ? numeroDeVale(v) : null;
    if (piso) {
      const dia = diaDeCarga(f);
      if (n === null) {
        // Sin número no se puede ubicar en la serie: cuenta como «sin vale»
        // si se cargó desde el día del reinicio. Sin fecha legible se cuenta:
        // en la duda, se dice de más.
        if (!dia || dia >= c.reinicio_dia) {
          if (v) noNumericos.push(v);
          else sinVale += 1;
        }
        continue;
      }
      if (n >= piso) {
        if (!numeros.has(n)) numeros.set(n, f);
      } else if (piso - n > LEJOS_DE_LA_SERIE && dia && dia >= c.reinicio_dia) {
        // Cargado DESPUÉS del reinicio y lejísimo por debajo del ancla: un
        // «956» por «9567», o un talonario nuevo que arranca más abajo. La
        // serie vieja queda a menos de mil y se cargó antes. Sin esto saldría
        // de la cuenta sin que nadie se entere.
        if (!abajo.has(n)) abajo.set(n, f);
      }
      continue;
    }
    if (!(Date.parse(f?.fecha) >= corteMs)) continue;
    if (fechaAdelantada(f) && diaIsoAr(f.creado_en) < c.desde_fecha) continue;
    if (!v) { sinVale += 1; continue; }
    if (n === null) { noNumericos.push(v); continue; }
    if (!numeros.has(n)) numeros.set(n, f);
  }

  const ordenados = [...numeros.keys()].sort((a, b) => a - b);
  let serie;
  let sueltos;
  let otros;
  if (piso) {
    const [seguidos, arriba] = sinSaltos(ordenados, piso);
    for (const n of arriba) abajo.set(n, numeros.get(n));
    serie = seguidos;
    sueltos = [];
    otros = [];
    for (const t of tramos([...abajo.keys()].sort((a, b) => a - b))) {
      if (t.length >= MIN_TALONARIO) otros.push(t);
      else sueltos.push(...t);
    }
  } else {
    [serie, sueltos, otros] = separarSerie(
      ordenados, () => (recientes ? topeDeLaSerie(recientes) : null));
  }
  const filaDe = (n) => numeros.get(n) || abajo.get(n) || null;

  const otrosTalonarios = otros.map((t) => ({
    desde: t[0], hasta: t[t.length - 1], vales: t.length,
    ultimo_id: ultimoId(t.map(filaDe)),
  })).sort((x, y) => x.desde - y.desde);
  /* `reiniciar_desde`: si otro talonario tiene una carga más reciente (por
     `id`) que cualquiera de la serie, su primer número: es el que se está
     cargando y «/valesdeurea-reset desde el vale N» lo pasa a contar. */
  let reiniciarDesde = null;
  if (otrosTalonarios.length) {
    const masNuevo = otrosTalonarios.reduce((x, y) => (y.ultimo_id > x.ultimo_id ? y : x));
    if (masNuevo.ultimo_id > ultimoId(serie.map(filaDe))) reiniciarDesde = masNuevo.desde;
  }

  const base = {
    desde_fecha: c.desde_fecha,
    desde_vale: piso,
    reinicio_dia: c.reinicio_dia || null,
    reinicio_por: c.reinicio_por || null,
    corte_sin_leer: !!c.sin_leer,
    sin_vale: sinVale,
    no_numericos: noNumericos,
    // Los que no siguen la serie: van en el resultado para poder decirlos, y
    // con su carga para poder abrirla. Un vale que sale de la cuenta sin
    // explicación es un vale escondido.
    fuera_de_serie: [...sueltos].sort((a, b) => a - b),
    fuera_de_serie_filas: [...sueltos].sort((a, b) => a - b).map((n) => ({
      numero: n, id: filaDe(n)?.id ?? null, vale: filaDe(n)?.vale ?? String(n),
      cargado: diaIsoAr(filaDe(n)?.creado_en) || null,
    })),
    otros_talonarios: otrosTalonarios,
    reiniciar_desde: reiniciarDesde,
    anulados_sin_leer: anulados == null,
  };
  if (!serie.length) {
    return { ...base, desde: null, hasta: null, cargados: 0, esperados: 0,
             faltan: [], anulados: [] };
  }

  const lo = piso ?? serie[0];
  const hi = serie[serie.length - 1];
  const hay = new Set(serie);
  const anul = new Set((anulados || [])
    .map((a) => Math.trunc(Number(a && typeof a === "object" ? a.numero : a)))
    .filter(Number.isFinite));
  const faltan = [];
  for (let i = lo; i <= hi; i++) {
    if (!hay.has(i) && !anul.has(i)) faltan.push(i);
  }
  return {
    ...base,
    desde: lo,
    hasta: hi,
    cargados: serie.length,
    esperados: hi - lo + 1,
    faltan,
    // Los que se sacaron por anulados: para poder decirlos.
    anulados: [...anul].filter((n) => n >= lo && n <= hi).sort((a, b) => a - b),
  };
}

/* El comando exacto para contar desde otro talonario, si es el que se está
   cargando: «/valesdeurea-reset desde el vale 20001», con «rgd» en el de Río
   Grande y con «igual» si todavía tiene menos vales de los que el comando
   reconoce como talonario. Es lo que dice el parte. `null` si no hay. */
export function comandoReinicio(cuenta) {
  const n = cuenta?.reiniciar_desde;
  if (!n) return null;
  const t = (cuenta.otros_talonarios || []).find((o) => o.desde === n);
  return "/valesdeurea-reset" + (cuenta.id === RGD ? " rgd" : "") +
    ` desde el vale ${n}` + (t && t.vales < MIN_TRAMO_COMANDO ? " igual" : "");
}

/* «20001–20007 (7 vales)», separados por coma (`urea.texto_otros_talonarios`). */
export function textoOtrosTalonarios(items) {
  return (items || []).map((t) => {
    const rango = t.desde === t.hasta ? `${t.desde}` : `${t.desde}–${t.hasta}`;
    return `${rango} (${t.vales} vale${t.vales === 1 ? "" : "s"})`;
  }).join(", ");
}

/* Los anulados vivos de UN talonario, de la lista entera. Mismo filtro que el
   bot: serie nula para el de acá, 'RGD' para el de allá. `null` sigue siendo
   «no se pudieron leer». */
export function anuladosDe(anulados, id) {
  if (anulados == null) return null;
  const serie = SERIE[id];
  return anulados.filter((a) => (serie === null ? a?.serie == null : a?.serie === serie));
}

/* La cuenta de los dos talonarios, en el orden de `TALONARIOS`.
 *
 * `filas`: `{ bue: [...], rgd: [...] }`, las cargas de cada uno.
 * `cortes`: lo que devolvió `urea_corte_vales()`, o `null`.
 * `anulados`: la lista de anulados vivos (con `serie` y `numero`), o `null`.
 * `recientes`: `{ bue: [...], rgd: [...] }`, las últimas cargas de cada uno
 *   por `id` (`null` en uno, o entero, si no se pudieron leer). */
export function cuentaDeVales({ filas = {}, cortes = null, anulados = null, recientes = null } = {}) {
  return TALONARIOS.filter((t) => t.id !== "todos").map((t) => ({
    id: t.id,
    rotulo: t.rotulo,
    ...contarTalonario(filas[t.id] || [], corteDe(cortes, t.id), {
      anulados: anuladosDe(anulados, t.id),
      recientes: recientes ? (recientes[t.id] ?? null) : null,
    }),
  }));
}

/* «Se cuenta desde el vale 9560 · reinicio del 11/10», o «desde el 08/10».
 *
 * Con ancla se dice el NÚMERO (como el parte): el conteo arranca en un vale,
 * no en un día, y «desde el 11/10» mandaría a buscar por fecha justo los
 * vales que el ancla cuenta sin mirarla. */
export function desdeDonde(cuenta) {
  if (!cuenta) return "";
  const dm = (d) => diaAr(d).slice(0, 5);
  if (cuenta.desde_vale) {
    return `Se cuenta desde el vale ${cuenta.desde_vale}` +
      (cuenta.reinicio_dia ? ` · reinicio del ${dm(cuenta.reinicio_dia)}` : "");
  }
  return `Se cuenta desde el ${dm(cuenta.desde_fecha || VALES_DESDE)}`;
}
